import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, XCircle, Sparkles,
  BookOpen, Code2, Brain, Trophy, PlayCircle, RotateCcw
} from 'lucide-react';
import { conceptsData } from '@/data/conceptsData';
import { getLocalizedConcept } from '@/data/localized';
import { useCourseLanguage, getLangInfo } from '@/data/languages';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { getLessonById } from '@/data/lessonsData';
import { logActivity } from '@/utils/api';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const ConceptLesson = () => {
  const { conceptId } = useParams();
  const navigate = useNavigate();
  const [lang] = useCourseLanguage();
  const concept = getLocalizedConcept(conceptId, lang);

  const [activeTab, setActiveTab] = useState('learn'); // learn | code | quiz
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [selectedExampleIdx, setSelectedExampleIdx] = useState(0);

  useEffect(() => {
    setQuizAnswers({});
    setShowResults(false);
    setSelectedExampleIdx(0);
  }, [lang]);

  if (!concept) {
    navigate('/dashboard');
    return null;
  }

  const currentIndex = conceptsData.findIndex(c => c.id === conceptId);
  const nextConcept = conceptsData[currentIndex + 1];
  const prevConcept = conceptsData[currentIndex - 1];

  const handleQuizAnswer = (qIdx, optIdx) => {
    if (showResults) return;
    setQuizAnswers({ ...quizAnswers, [qIdx]: optIdx });
  };

  const submitQuiz = () => {
    setShowResults(true);
    const score = concept.quiz.filter((q, i) => quizAnswers[i] === q.correctIndex).length;
    logActivity('quiz_completed', conceptId, score, concept.quiz.length);
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setShowResults(false);
  };

  const correctCount = concept.quiz.filter((q, i) => quizAnswers[i] === q.correctIndex).length;
  const allAnswered = Object.keys(quizAnswers).length === concept.quiz.length;

  const currentExample = concept.codeExamples[selectedExampleIdx];

  const tabs = [
    { id: 'learn', label: 'Learn', icon: BookOpen, testId: 'concept-tab-learn' },
    { id: 'code', label: 'Code Examples', icon: Code2, testId: 'concept-tab-code' },
    { id: 'quiz', label: 'Quiz', icon: Brain, testId: 'concept-tab-quiz' }
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Header />
      
      <main data-testid="concept-container" className="pt-24 pb-12 max-w-6xl mx-auto px-6 w-full flex-1">
        {/* Top Nav */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5"
            data-testid="concept-back-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2 text-[#71717A] text-sm font-code">
            <span className="text-[#FFB800]">Concept {concept.order}</span>
            <span>/</span>
            <span>{conceptsData.length}</span>
          </div>
        </div>

        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0F0F12] border border-white/10 rounded-xl p-8 mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Badge className="bg-green-500/10 text-green-400 border-green-500/20 border font-code text-xs">
              {concept.difficulty}
            </Badge>
            <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/20 border font-code text-xs">
              {getLangInfo(lang).label} Basics
            </Badge>
            <span className="text-[#71717A] text-xs font-code ml-auto">{concept.estimatedTime}</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white mb-2">{concept.title}</h1>
          <p className="text-[#A1A1AA] font-body text-lg mb-4">{concept.subtitle}</p>
          <LanguageSwitcher testIdPrefix="concept-lang" />
        </motion.div>

        {/* Tab Nav */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                data-testid={tab.testId}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-heading font-medium text-sm transition-colors border-b-2 ${
                  isActive
                    ? 'text-[#FFB800] border-[#FFB800]'
                    : 'text-[#71717A] border-transparent hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'quiz' && showResults && (
                  <Badge className={`ml-1 border font-code text-[10px] ${
                    correctCount === concept.quiz.length
                      ? 'bg-green-500/10 text-green-400 border-green-500/20'
                      : 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20'
                  }`}>
                    {correctCount}/{concept.quiz.length}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'learn' && (
            <motion.div
              key="learn"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6">
                <h3 className="text-[#FFB800] font-heading font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  What You'll Learn
                </h3>
                <p className="text-white font-body leading-relaxed">{concept.explanation}</p>
              </div>

              <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6">
                <h3 className="text-[#007AFF] font-heading font-semibold mb-4">Key Points</h3>
                <ul className="space-y-3">
                  {concept.keyPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#007AFF]/20 border border-[#007AFF]/40 flex items-center justify-center flex-shrink-0 text-[#007AFF] text-xs font-code font-bold">
                        {i + 1}
                      </div>
                      <span className="text-white font-body text-sm leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => setActiveTab('code')}
                  data-testid="concept-next-to-code"
                  className="bg-[#FFB800] hover:bg-[#FFC520] text-black font-heading font-semibold"
                >
                  See Code Examples
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {activeTab === 'code' && (
            <motion.div
              key="code"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Example Selector */}
              <div className="flex gap-2 flex-wrap">
                {concept.codeExamples.map((ex, i) => (
                  <button
                    key={i}
                    data-testid={`concept-example-${i}`}
                    onClick={() => setSelectedExampleIdx(i)}
                    className={`px-4 py-2 rounded-lg border font-code text-xs transition-colors ${
                      selectedExampleIdx === i
                        ? 'bg-[#FFB800]/10 border-[#FFB800] text-[#FFB800]'
                        : 'bg-[#0F0F12] border-white/10 text-[#A1A1AA] hover:border-white/30'
                    }`}
                  >
                    {ex.title}
                  </button>
                ))}
              </div>

              {/* Code Panel */}
              <div className="bg-[#0A0A0A] rounded-xl border border-white/10 overflow-hidden">
                <div className="bg-[#0F0F12] px-4 py-3 border-b border-white/10 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-[#FFB800]" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-[#71717A] text-xs font-code ml-2">{currentExample.title}.{getLangInfo(lang).file.split('.').pop()}</span>
                </div>
                <pre className="p-6 overflow-x-auto">
                  <code className="text-white font-code text-sm leading-relaxed whitespace-pre">
                    {currentExample.code}
                  </code>
                </pre>
              </div>

              {/* Output */}
              <div className="bg-[#0F0F12] border border-green-500/20 rounded-xl p-4">
                <div className="text-green-400 text-xs font-code mb-2 flex items-center gap-2">
                  <PlayCircle className="w-4 h-4" />
                  OUTPUT
                </div>
                <pre className="text-white font-code text-sm whitespace-pre-wrap">
                  {currentExample.output}
                </pre>
              </div>

              <div className="flex justify-between">
                <Button
                  onClick={() => setActiveTab('learn')}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Learn
                </Button>
                <Button
                  onClick={() => setActiveTab('quiz')}
                  data-testid="concept-next-to-quiz"
                  className="bg-[#FFB800] hover:bg-[#FFC520] text-black font-heading font-semibold"
                >
                  Take the Quiz
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {activeTab === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {concept.quiz.map((q, qIdx) => {
                const userAns = quizAnswers[qIdx];
                const isCorrect = userAns === q.correctIndex;

                return (
                  <div
                    key={qIdx}
                    data-testid={`quiz-question-${qIdx}`}
                    className="bg-[#0F0F12] border border-white/10 rounded-xl p-6"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-[#FFB800]/20 border border-[#FFB800]/40 flex items-center justify-center flex-shrink-0 text-[#FFB800] font-code font-bold">
                        {qIdx + 1}
                      </div>
                      <h3 className="text-white font-body font-medium leading-relaxed whitespace-pre-wrap">{q.question}</h3>
                    </div>

                    <div className="space-y-2 ml-11">
                      {q.options.map((opt, optIdx) => {
                        const isSelected = userAns === optIdx;
                        const isCorrectAnswer = optIdx === q.correctIndex;
                        let optClass = 'bg-[#1A1A20] border-white/10 text-white hover:border-white/30';
                        
                        if (showResults) {
                          if (isCorrectAnswer) {
                            optClass = 'bg-green-500/10 border-green-500 text-green-400';
                          } else if (isSelected && !isCorrectAnswer) {
                            optClass = 'bg-red-500/10 border-red-500 text-red-400';
                          } else {
                            optClass = 'bg-[#1A1A20] border-white/10 text-[#71717A]';
                          }
                        } else if (isSelected) {
                          optClass = 'bg-[#FFB800]/10 border-[#FFB800] text-[#FFB800]';
                        }

                        return (
                          <button
                            key={optIdx}
                            data-testid={`quiz-option-${qIdx}-${optIdx}`}
                            onClick={() => handleQuizAnswer(qIdx, optIdx)}
                            disabled={showResults}
                            className={`w-full text-left px-4 py-3 rounded-lg border-2 font-code text-sm transition-all flex items-center gap-3 ${optClass} ${!showResults && 'cursor-pointer'}`}
                          >
                            <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ borderColor: 'currentColor' }}>
                              {String.fromCharCode(65 + optIdx)}
                            </div>
                            <span className="flex-1 whitespace-pre-wrap">{opt}</span>
                            {showResults && isCorrectAnswer && <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />}
                            {showResults && isSelected && !isCorrectAnswer && <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>

                    {showResults && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className={`mt-4 ml-11 p-3 rounded-lg border ${
                          isCorrect ? 'bg-green-500/5 border-green-500/30' : 'bg-[#007AFF]/5 border-[#007AFF]/30'
                        }`}
                      >
                        <div className="text-xs font-code mb-1" style={{ color: isCorrect ? '#4ADE80' : '#60A5FA' }}>
                          {isCorrect ? '✓ CORRECT' : '💡 EXPLANATION'}
                        </div>
                        <div className="text-white text-sm font-body leading-relaxed">{q.explanation}</div>
                      </motion.div>
                    )}
                  </div>
                );
              })}

              {/* Quiz Actions */}
              {!showResults ? (
                <div className="flex justify-between items-center bg-[#0F0F12] border border-white/10 rounded-xl p-4">
                  <div className="text-[#A1A1AA] text-sm font-code">
                    {Object.keys(quizAnswers).length} / {concept.quiz.length} answered
                  </div>
                  <Button
                    data-testid="concept-submit-quiz"
                    onClick={submitQuiz}
                    disabled={!allAnswered}
                    className="bg-[#FFB800] hover:bg-[#FFC520] text-black font-heading font-semibold disabled:opacity-50"
                  >
                    Submit Quiz
                  </Button>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`bg-gradient-to-r ${
                    correctCount === concept.quiz.length
                      ? 'from-green-500/10 to-green-400/10 border-green-500/30'
                      : 'from-[#FFB800]/10 to-[#FF8800]/10 border-[#FFB800]/30'
                  } border rounded-xl p-6 space-y-4`}
                >
                  <div className="flex items-center gap-3">
                    <Trophy className={`w-8 h-8 ${correctCount === concept.quiz.length ? 'text-green-400' : 'text-[#FFB800]'}`} />
                    <div>
                      <div className="text-white font-heading text-xl font-bold">
                        {correctCount === concept.quiz.length ? 'Perfect Score!' : `You got ${correctCount}/${concept.quiz.length}`}
                      </div>
                      <div className="text-[#A1A1AA] text-sm font-body">
                        {correctCount === concept.quiz.length
                          ? 'You\'ve mastered this concept. Time to practice!'
                          : 'Review the explanations above, then continue learning.'}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <Button
                      onClick={resetQuiz}
                      variant="outline"
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Practice with Related Problems */}
              {showResults && concept.relatedProblems.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0F0F12] border border-[#FFB800]/20 rounded-xl p-6"
                >
                  <h3 className="text-[#FFB800] font-heading font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Now Practice with These Problems
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {concept.relatedProblems.map(problemId => {
                      const problem = getLessonById(problemId);
                      if (!problem) return null;
                      return (
                        <button
                          key={problemId}
                          data-testid={`related-problem-${problemId}`}
                          onClick={() => navigate(`/lesson/${problemId}`)}
                          className="bg-[#1A1A20] border border-white/10 hover:border-[#FFB800]/50 hover:bg-[#FFB800]/5 rounded-lg p-4 text-left transition-all group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="text-white font-heading font-medium group-hover:text-[#FFB800] transition-colors">
                              {problem.title}
                            </div>
                            <ArrowRight className="w-4 h-4 text-[#71717A] group-hover:text-[#FFB800] transition-colors flex-shrink-0" />
                          </div>
                          <div className="text-[#A1A1AA] text-xs font-body">{problem.concept}</div>
                          <div className="text-[#71717A] text-xs font-code mt-1">{problem.module} · {problem.difficulty}</div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prev / Next Concept Nav */}
        <div className="mt-8 flex items-center justify-between gap-4">
          {prevConcept ? (
            <Button
              data-testid="concept-prev"
              onClick={() => { navigate(`/concept/${prevConcept.id}`); setActiveTab('learn'); setQuizAnswers({}); setShowResults(false); }}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5 flex-1 justify-start"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <div className="text-left">
                <div className="text-[10px] text-[#71717A] font-code">PREVIOUS</div>
                <div className="text-sm font-heading">{prevConcept.title}</div>
              </div>
            </Button>
          ) : <div className="flex-1" />}
          {nextConcept ? (
            <Button
              data-testid="concept-next"
              onClick={() => { navigate(`/concept/${nextConcept.id}`); setActiveTab('learn'); setQuizAnswers({}); setShowResults(false); }}
              className="bg-[#FFB800] hover:bg-[#FFC520] text-black flex-1 justify-end"
            >
              <div className="text-right">
                <div className="text-[10px] opacity-70 font-code">NEXT</div>
                <div className="text-sm font-heading">{nextConcept.title}</div>
              </div>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#FFB800] hover:bg-[#FFC520] text-black flex-1 justify-end"
            >
              <div className="text-right">
                <div className="text-[10px] opacity-70 font-code">DONE</div>
                <div className="text-sm font-heading">Explore Problems</div>
              </div>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ConceptLesson;
